import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { FocusTrap } from 'focus-trap-react';
import { Icon, Tool } from '../basic';
import {
  Breakpoints,
  intl,
  useBreakpoint,
  useHideNavigationWhileOpen
} from '../../lib';
import { useOpenedModalsStore } from '../../lib/OpenedModals.store';

export type VerticalTabsItem = {
  id: string;
  title: string | (() => React.JSX.Element);
  link: () => React.JSX.Element;
  render: () => React.JSX.Element;
};

export type VerticalTabsProps = {
  title: string;
  hideTitle?: boolean;
  items: VerticalTabsItem[];
  mobileItemPreview?: React.JSX.Element;
  unselectedState?: React.JSX.Element;
  listFooter?: React.JSX.Element;
  selectedItemId?: string;
  onSelectedItemIdChange: (selectedItemId?: string) => void;
  className?: string;
  contentAriaRole?: 'region' | 'form';
  /** If you need the back button to do something different from the default behavior, you can use this property. */
  onBack?: () => void;
};

const focusableContentSelector = [
  'button:not(:disabled)',
  '[href]',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

function findFocusableElement(
  container: HTMLElement | null
): HTMLElement | null {
  return container?.querySelector(focusableContentSelector) ?? null;
}

/**
 * A list-detail layout rendered with an accessible vertical tabs pattern (ARIA APG-compliant).
 *
 * The visual layout matches {@link ListDetail}, but the left side is exposed as a proper `tablist`
 * with each item as a `tab` and keyboard interaction aligned with the ARIA Authoring Practices Guide.
 *
 * ## When to use
 * - Switching between multiple related, peer-level panels/forms (e.g., settings categories, form sections, views)
 * - Keyboard accessibility requirements include arrow keys, Home, End navigation
 * - Selection state should be controlled by the parent component (via `selectedItemId` + `onSelectedItemIdChange`)
 * - Full ARIA tabs semantics are required
 *
 * ## When NOT to use
 * - If you're displaying detail content for items selected from a list → use `ListDetail` instead
 *   (ListDetail provides simpler `aria-expanded` pattern and manages selection state internally)
 *
 * ## Accessibility
 * - Full ARIA tabs pattern: `role="tablist"`, `role="tab"`, `role="tabpanel"`
 * - Keyboard navigation: Arrow Down/Right (next tab), Arrow Up/Left (previous tab), Home (first), End (last), Enter/Space (activate)
 * - Mobile: FocusTrap ensures focus stays within the active panel; back button closes panel and restores focus to tab
 * - Automatic focus management: moves to first focusable element in panel or back button on mobile
 */
export function VerticalTabs({
  title,
  hideTitle,
  items,
  mobileItemPreview,
  unselectedState,
  listFooter,
  selectedItemId,
  onSelectedItemIdChange,
  contentAriaRole = 'region',
  onBack: customOnBack,
  ...props
}: Readonly<VerticalTabsProps>): React.JSX.Element {
  const isMobileView = useBreakpoint(Breakpoints.MOBILE);
  const hasOpenedModals = useOpenedModalsStore(
    state => state.openedModalsIds.length > 0
  );
  const [focusedItemId, setFocusedItemId] = useState<string | undefined>(
    selectedItemId ?? items[0]?.id
  );
  const [previewImageVisible, setPreviewImageVisible] = useState(true);
  const [panelHasFocusableContent, setPanelHasFocusableContent] = useState(
    false
  );
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const rightPanelRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const panelContentRef = useRef<HTMLDivElement | null>(null);
  const backButtonRef = useRef<HTMLButtonElement | null>(null);
  const shouldMoveFocusToPanel = useRef(false);
  const restoreTabFocusToId = useRef<string | null>(null);
  const tabsTitleId = useId();
  const tabsBaseId = useId();
  const detailViewAriaDescriptionId = useId();

  const selectedItem = useMemo(
    () => items.find(item => item.id === selectedItemId),
    [items, selectedItemId]
  );
  useHideNavigationWhileOpen(isMobileView && selectedItem !== undefined);
  useEffect(() => {
    setFocusedItemId(currentFocusedItemId => {
      if (
        currentFocusedItemId &&
        items.some(item => item.id === currentFocusedItemId)
      ) {
        return currentFocusedItemId;
      }
      if (selectedItemId && items.some(item => item.id === selectedItemId)) {
        return selectedItemId;
      }
      return items[0]?.id;
    });
  }, [items, selectedItemId]);

  const activateItem = useCallback(
    (item: VerticalTabsItem, moveFocusToPanel = false) => {
      setFocusedItemId(item.id);
      shouldMoveFocusToPanel.current = moveFocusToPanel;
      onSelectedItemIdChange(item.id);
    },
    [onSelectedItemIdChange]
  );

  const focusItemAtIndex = useCallback(
    (index: number) => {
      const targetItem = items[index];
      if (!targetItem) {
        return;
      }
      setFocusedItemId(targetItem.id);
      tabRefs.current[targetItem.id]?.focus();
    },
    [items]
  );

  const handleTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, item: VerticalTabsItem) => {
      const currentIndex = items.findIndex(
        currentItem => currentItem.id === item.id
      );
      if (currentIndex === -1 || items.length === 0) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          event.preventDefault();
          focusItemAtIndex((currentIndex + 1) % items.length);
          return;
        case 'ArrowUp':
        case 'ArrowLeft':
          event.preventDefault();
          focusItemAtIndex((currentIndex - 1 + items.length) % items.length);
          return;
        case 'Home':
          event.preventDefault();
          focusItemAtIndex(0);
          return;
        case 'End':
          event.preventDefault();
          focusItemAtIndex(items.length - 1);
          return;
        case 'Enter':
        case ' ':
          event.preventDefault();
          activateItem(item, isMobileView);
      }
    },
    [activateItem, focusItemAtIndex, isMobileView, items]
  );

  const mobileItemPreviewRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element || !isMobileView) {
        setPreviewImageVisible(false);
        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          setPreviewImageVisible(entry.isIntersecting);
        },
        { threshold: 0 }
      );

      observer.observe(element);

      return () => {
        observer.disconnect();
      };
    },
    [isMobileView]
  );

  useEffect(() => {
    if (rightPanelRef.current) {
      rightPanelRef.current.scrollTop = 0;
    }
  }, [selectedItemId]);

  useEffect(() => {
    const container = panelContentRef.current;
    if (!selectedItem || !container) {
      setPanelHasFocusableContent(false);
      return;
    }

    const updateFocusableState = () => {
      setPanelHasFocusableContent(findFocusableElement(container) !== null);
    };

    updateFocusableState();

    const observer = new MutationObserver(updateFocusableState);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'tabindex', 'href']
    });

    return () => {
      observer.disconnect();
    };
  }, [selectedItem]);

  useEffect(() => {
    if (!isMobileView || !selectedItem || !shouldMoveFocusToPanel.current) {
      return;
    }

    const nextFocusableElement =
      findFocusableElement(panelContentRef.current) ??
      backButtonRef.current ??
      panelRef.current;

    nextFocusableElement?.focus();
    shouldMoveFocusToPanel.current = false;
  }, [isMobileView, selectedItem]);

  useEffect(() => {
    if (!restoreTabFocusToId.current) {
      return;
    }

    const targetTab = tabRefs.current[restoreTabFocusToId.current];
    restoreTabFocusToId.current = null;
    targetTab?.focus();
  }, [selectedItemId]);

  const handleBackToTabList = useCallback(() => {
    restoreTabFocusToId.current = selectedItemId ?? focusedItemId ?? null;
    shouldMoveFocusToPanel.current = false;
    onSelectedItemIdChange(undefined);
  }, [focusedItemId, onSelectedItemIdChange, selectedItemId]);

  const onBack = useCallback(() => {
    if (customOnBack) {
      customOnBack();
    } else {
      handleBackToTabList();
    }
  }, [customOnBack, handleBackToTabList]);

  const leftPaneClassName = [
    'ehs-vertical-tabs-left',
    isMobileView && selectedItem ? 'mobile-hidden' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      {...props}
      className={
        'ehs-vertical-tabs' + (props.className ? ` ${props.className}` : '')
      }
    >
      {items.length > 0 && (
        <div className={leftPaneClassName}>
          {isMobileView && mobileItemPreview && (
            <div
              ref={mobileItemPreviewRef}
              className="ehs-vertical-tabs-left-preview"
            >
              {mobileItemPreview}
            </div>
          )}

          <div
            className={
              'ehs-vertical-tabs-left-header-container' +
              (isMobileView && !hideTitle ? '' : ' hide')
            }
          >
            <div
              className={
                'ehs-vertical-tabs-left-header' +
                (mobileItemPreview ? ' preview-available' : '') +
                (previewImageVisible ? ' preview-visible' : '')
              }
            >
              <span
                id={tabsTitleId}
                className={'sh-font' + (isMobileView ? '' : 'hide')}
                /* Serve as a placeholder text for mobile view */
                aria-hidden={true}
              >
                {title}
              </span>
            </div>
          </div>

          <div
            className="ehs-vertical-tabs-tablist"
            role="tablist"
            aria-labelledby={tabsTitleId}
            aria-orientation="vertical"
          >
            {items.map(item => {
              const tabId = `${tabsBaseId}-tab-${item.id}`;
              const panelId = `${tabsBaseId}-tabpanel-${item.id}`;
              const isSelected = selectedItem?.id === item.id;
              const isFocusable = focusedItemId === item.id;
              return (
                <button
                  key={'vertical-tabs-item-' + item.id}
                  ref={element => {
                    tabRefs.current[item.id] = element;
                  }}
                  id={tabId}
                  type="button"
                  role="tab"
                  className={
                    'ehs-vertical-tabs-item' + (isSelected ? ' selected' : '')
                  }
                  data-testid={item.id}
                  aria-selected={isSelected}
                  aria-controls={panelId}
                  tabIndex={isFocusable ? 0 : -1}
                  onClick={() => activateItem(item, isMobileView)}
                  onFocus={() => setFocusedItemId(item.id)}
                  onKeyDown={event => handleTabKeyDown(event, item)}
                >
                  <div className="ehs-vertical-tabs-item-link">
                    {item.link()}
                  </div>
                  <Icon
                    iconName="shui_arrow-line-right-mini"
                    aria-label={undefined}
                  />
                </button>
              );
            })}
            {listFooter}
          </div>
        </div>
      )}
      {(!isMobileView || selectedItem !== undefined || items.length === 0) && (
        <div
          ref={rightPanelRef}
          className={
            'ehs-vertical-tabs-right' + (selectedItem ? '' : ' empty-state')
          }
        >
          {selectedItem ? (
            <FocusTrap
              active={isMobileView && !hasOpenedModals}
              focusTrapOptions={{
                escapeDeactivates: false,
                initialFocus: false,
                returnFocusOnDeactivate: false,
                fallbackFocus: `#${tabsBaseId}-tabpanel-${selectedItem.id}`,
                allowOutsideClick: (e: MouseEvent | TouchEvent) => {
                  const clickedElement = e.target as HTMLElement | null;
                  return (
                    clickedElement?.closest(
                      '.ehs-modal, .ehs-modal-overlay, .ehs-toasts-container'
                    ) !== null
                  );
                }
              }}
            >
              <div
                ref={panelRef}
                id={`${tabsBaseId}-tabpanel-${selectedItem.id}`}
                className="ehs-vertical-tabs-item-control-container"
                role="tabpanel"
                aria-labelledby={`${tabsBaseId}-tab-${selectedItem.id}`}
                tabIndex={panelHasFocusableContent ? -1 : 0}
              >
                <div className="ehs-vertical-tabs-item-header">
                  {isMobileView && (
                    <Tool
                      ref={backButtonRef}
                      icon="shui_arrow-line-left-mini"
                      className="ehs-vertical-tabs-item-header-spacer"
                      onClick={onBack}
                      tooltip={intl('view.button.back')}
                    />
                  )}
                  {typeof selectedItem.title === 'string' ? (
                    <h3 id={detailViewAriaDescriptionId} className="sh-font">
                      {selectedItem.title}
                    </h3>
                  ) : (
                    <div id={detailViewAriaDescriptionId}>
                      {selectedItem.title()}
                    </div>
                  )}
                  {isMobileView && (
                    <div className="ehs-vertical-tabs-item-header-spacer"></div>
                  )}
                </div>
                <div
                  ref={panelContentRef}
                  className="ehs-vertical-tabs-item-control"
                  role={contentAriaRole}
                  aria-labelledby={detailViewAriaDescriptionId}
                >
                  {selectedItem.render()}
                </div>
              </div>
            </FocusTrap>
          ) : (
            unselectedState
          )}
        </div>
      )}
    </div>
  );
}
